import React, { useEffect, useState } from 'react';
import { SlidersHorizontal, X, RefreshCw } from 'lucide-react';
import { resolveContactIdentity } from '../../../api/contact';
import ContactRentingPreferencesEditor from './ContactRentingPreferencesEditor';

/**
 * Renting Preferences editor in a modal - the rental-side counterpart to
 * BuyerPreferencesModal. Resolves (get-or-create) a CRM Contact from the
 * passed identity, then mounts ContactRentingPreferencesEditor, which writes
 * to a separate RentingPreference record so renter demand is tracked apart
 * from buyer preferences. Pass any of {contactId, email, name, phone, buyerId}.
 *
 * Rendered conditionally by the parent: mount it only when `identity` is set,
 * and clear that state in `onClose`.
 */
const RentingPreferencesModal = ({ identity, title, onClose, onSaved }) => {
  const [resolving, setResolving] = useState(false);
  const [contactId, setContactId] = useState(identity?.contactId || null);

  useEffect(() => {
    let active = true;
    if (identity?.contactId) {
      setContactId(identity.contactId);
      return undefined;
    }
    (async () => {
      setResolving(true);
      try {
        const res = await resolveContactIdentity(identity);
        if (active) setContactId(res.data?.contact_id || null);
      } catch (e) {
        // Editor shows a "couldn't open" state if this stays null.
      } finally {
        if (active) setResolving(false);
      }
    })();
    return () => { active = false; };
  }, [identity]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onMouseDown={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <SlidersHorizontal className="w-4 h-4 text-[#3a99a3] shrink-0" />
            <span className="text-sm font-semibold text-gray-900 truncate">
              Renter preferences{title ? ` · ${title}` : ''}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {resolving ? (
            <div className="flex items-center gap-2 justify-center py-16 text-sm text-gray-500">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Opening preferences…
            </div>
          ) : contactId ? (
            <ContactRentingPreferencesEditor
              contactId={contactId}
              onSaved={(snapshot) => {
                onSaved?.(snapshot);
                onClose?.();
              }}
            />
          ) : (
            <div className="py-16 text-center text-sm text-gray-500">
              Couldn&apos;t open this renter&apos;s preferences. Please try again.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RentingPreferencesModal;
